import { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { Client } from '@stomp/stompjs';
import SockJS from 'sockjs-client';
import { toast } from 'react-toastify';
import { useAuth } from '../hooks/useAuth';
import api from '../utils/api';

const DEFAULT_SCORE = {
  teamAScore: 0,
  teamBScore: 0,
  teamAName: 'Team A',
  teamBName: 'Team B',
  status: 'INITIALIZING',
  isGameEnded: false
};

const MAX_RECONNECT_ATTEMPTS = 3;
const RECONNECT_DELAY = 5000;

const useScoreboard = (livestreamId) => {
  const { user, isAuthenticated } = useAuth();
  const [score, setScore] = useState(DEFAULT_SCORE);
  const [isInitialized, setIsInitialized] = useState(false);
  const prevScoreRef = useRef(null);
  const stompClientRef = useRef(null);
  const retryTimeoutRef = useRef(null);
  const reconnectAttemptsRef = useRef(0);
  const endpoint = process.env.REACT_APP_WEBSOCKET_ENDPOINT || 'http://localhost:8080/ws';

  // Memoize the livestreamId to prevent unnecessary reconnections
  const memoizedLivestreamId = useMemo(() => livestreamId, [livestreamId]);

  const connectWebSocket = useCallback(() => {
    if (!isAuthenticated || !memoizedLivestreamId) {
      setScore({ ...DEFAULT_SCORE, status: 'UNAUTHENTICATED', error: 'User not authenticated' });
      setIsInitialized(true);
      return;
    }

    // Don't reconnect if we already have an active connection
    if (stompClientRef.current?.connected) {
  
      return;
    }

    // Reset reconnect attempts if we're explicitly connecting
    reconnectAttemptsRef.current = 0;

    setScore(prev => ({ ...prev, status: 'INITIALIZING', error: null }));

    // Clean up any existing connection only if it's not connected
    if (stompClientRef.current && !stompClientRef.current.connected) {
      stompClientRef.current.deactivate();
      stompClientRef.current = null;
    }

    // Create new SockJS connection with timeout and credentials
    const socket = new SockJS(endpoint, null, {
      timeout: 5000,
      transports: ['websocket', 'xhr-streaming', 'xhr-polling'],
      withCredentials: true
    });

    const stompClient = new Client({
      webSocketFactory: () => socket,
      debug: (str) => {
    
      },

      heartbeatIncoming: 4000,
      heartbeatOutgoing: 4000,
      onConnect: () => {
    
        setScore(prev => ({ ...prev, status: 'ACTIVE', error: null }));
        setIsInitialized(true);
        reconnectAttemptsRef.current = 0;

        // Subscribe to scoreboard updates
        stompClient.subscribe(`/topic/scoreboard/${memoizedLivestreamId}`, (message) => {
          try {
            const data = JSON.parse(message.body);

            // Handle game end
            if (data.status === 'STREAM_ENDED') {
              setScore(prev => ({
                ...prev,
                status: 'ENDED',

                error: null
              }));
              toast.info('Game has ended!', { autoClose: 5000 });
              return;
            }

            // Handle regular score update with smart merging
            setScore(prev => {
              const isReset = data.status === 'RESET' || data.reset === true;

              const safeScore = (incoming, current) =>
                incoming === undefined ? current
                : isReset ? incoming   // allow drop to 0 on reset
                : incoming < current ? current    // stale – ignore
                : incoming;           // newer – accept

              return {
                ...prev,
                teamAScore: safeScore(data.teamAScore, prev.teamAScore),
                teamBScore: safeScore(data.teamBScore, prev.teamBScore),
                teamAName: data.teamAName ?? prev.teamAName,
                teamBName: data.teamBName ?? prev.teamBName,
                status: isReset ? 'ACTIVE' : 'ACTIVE',

              error: null
              };
            });

          } catch (e) {
            setScore(prev => ({ ...prev, status: 'ERROR', error: 'Failed to parse score update' }));
          }
        });
      },
      onStompError: (frame) => {
        handleConnectionError('STOMP connection error: ' + (frame.headers['message'] || 'Unknown error'));
      },
      onWebSocketError: (error) => {
        handleConnectionError('WebSocket connection error: ' + error.message);
      },
      onDisconnect: () => {

        if (reconnectAttemptsRef.current < MAX_RECONNECT_ATTEMPTS) {
      
          reconnectAttemptsRef.current += 1;
          retryTimeoutRef.current = setTimeout(connectWebSocket, RECONNECT_DELAY);
        } else {
          handleConnectionError('Failed to connect to scoreboard after multiple attempts');
        }
      }
    });

    const handleConnectionError = (errorMessage) => {
      setScore(prev => ({ 
        ...prev, 
        status: 'ERROR', 
        error: errorMessage 
      }));
      setIsInitialized(true);
      
      if (reconnectAttemptsRef.current === 0) {
        toast.error('Scoreboard connection failed. Updates may be delayed.');
      }
    };

    try {
      stompClient.activate();
      stompClientRef.current = stompClient;
    } catch (error) {
      handleConnectionError('Failed to initialize scoreboard connection');
    }
  }, [memoizedLivestreamId, endpoint, isAuthenticated]);

  // Effect to handle connection lifecycle
  useEffect(() => {
    if (memoizedLivestreamId && isAuthenticated) {
      connectWebSocket();
    } else {
      if (stompClientRef.current) {

        stompClientRef.current.deactivate();
        stompClientRef.current = null;
      }
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      setScore(memoizedLivestreamId ? 
        { ...DEFAULT_SCORE, status: 'UNAUTHENTICATED', error: 'User not authenticated' } : 
        DEFAULT_SCORE
      );
      setIsInitialized(true);
    }

    return () => {
      if (retryTimeoutRef.current) {
        clearTimeout(retryTimeoutRef.current);
      }
      if (stompClientRef.current) {

        stompClientRef.current.deactivate();
        stompClientRef.current = null;
      }
    };
  }, [memoizedLivestreamId, connectWebSocket, isAuthenticated]);

  const updateScore = useCallback(async (team) => {
    if (!memoizedLivestreamId) {
      toast.warn('Scoreboard not connected. Please refresh or try again.');
      return;
    }

    if (score.isGameEnded) {
      toast.warn('Game has ended. Cannot update score.');
      return;
    }

    try {
      // Optimistic update
      // setScore(prev => ({
      //   ...prev,
      //   [`${team}Score`]: prev[`${team}Score`] + 1,
      // }));

      // Map team to correct endpoint suffix
      const teamEndpoint = team === 'teamA' ? 'teamA' : 'teamB';
      const response = await api.post(`/livestream/${memoizedLivestreamId}/score/${teamEndpoint}`);
      
      if (response.status === 401) {
        // Handle authentication error
        toast.error('Your session has expired. Please log in again.');
        // Roll back optimistic update
        setScore(prev => ({
          ...prev,
          [`${team}Score`]: Math.max(prev[`${team}Score`] - 1, 0),
        }));
        return;
      }

    } catch (error) {
      // Roll back optimistic update
      setScore(prev => ({
        ...prev,
        [`${team}Score`]: Math.max(prev[`${team}Score`] - 1, 0),
      }));

      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error('Failed to update score. Please try again.');
      }
    }
  }, [memoizedLivestreamId, score.isGameEnded]);

  const resetScore = useCallback(async () => {
    if (!memoizedLivestreamId) {
      toast.warn('Scoreboard not connected. Please refresh or try again.');
      return;
    }

    if (score.isGameEnded) {
      toast.warn('Game has ended. Cannot reset score.');
      return;
    }

    try {
      const response = await api.post(`/livestream/${memoizedLivestreamId}/score/reset`);
      
      if (response.status === 401) {
        toast.error('Your session has expired. Please log in again.');
        return;
      }

    } catch (error) {
      if (error.response?.status === 401) {
        toast.error('Your session has expired. Please log in again.');
      } else {
        toast.error('Failed to reset score. Please try again.');
      }
    }
  }, [memoizedLivestreamId, score.isGameEnded]);

  // Effect for score change notifications
  useEffect(() => {
    if (!score || !prevScoreRef.current || score.status !== 'ACTIVE') {
      prevScoreRef.current = score;
      return;
    }

    if (score.teamAScore > prevScoreRef.current.teamAScore) {
      toast.success(`${score.teamAName} scored!`, {
        autoClose: 2000,

      });
    }
    if (score.teamBScore > prevScoreRef.current.teamBScore) {
      toast.success(`${score.teamBName} scored!`, {
        autoClose: 2000,

      });
    }

    prevScoreRef.current = score;
  }, [score]);

  return { 
    score, 
    updateScore, 
    resetScore,
    isInitialized,
    status: score.status,
    isGameEnded: score.isGameEnded
  };
};

export default useScoreboard;
